import {
  BellRing,
  Building2,
  ClipboardCheck,
  Clock3,
  FilePenLine,
  FileSearch,
  FolderSearch,
  LoaderCircle,
  type LucideIcon,
  MessageSquareMore,
  MinusCircle,
  PhoneCall,
  Search,
  ShieldCheck,
  Siren,
  Sparkles,
} from 'lucide-react';
import { type SyntheticEvent, useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { CrashingOutRunDetailsModal } from '@/components/agentflow/CrashingOutRunDetailsModal';
import { useCrashingOutSource } from '@/components/agentflow/useCrashingOutSource';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  useGetCrashingOutPendingReviewsQuery,
  useGetCrashingOutStatusQuery,
} from '@/services/api/agentflowService';
import type { Task } from '@/services/types/client-intake-types';

const STATUS_LABELS: Record<'working' | 'needs_review' | 'not_running', string> = {
  working: 'Agent working',
  needs_review: 'Agent needs review',
  not_running: 'Agent not running',
};

type AgentStatus = NonNullable<Task['crashing_out_agent_status']>;

const TOOL_PRESENTATIONS: Record<
  string,
  { label: string; icon: LucideIcon; activeClassName: string; idleClassName: string }
> = {
  match_carrier_directory: {
    label: 'Matching carrier',
    icon: Building2,
    activeClassName: 'bg-sky-100 text-sky-700 border-sky-200',
    idleClassName: 'bg-sky-50 text-sky-500 border-sky-100',
  },
  send_voice_ai_call: {
    label: 'Calling insurer',
    icon: PhoneCall,
    activeClassName: 'bg-cyan-100 text-cyan-700 border-cyan-200',
    idleClassName: 'bg-cyan-50 text-cyan-500 border-cyan-100',
  },
  send_bpo_call: {
    label: 'Calling insurer',
    icon: PhoneCall,
    activeClassName: 'bg-cyan-100 text-cyan-700 border-cyan-200',
    idleClassName: 'bg-cyan-50 text-cyan-500 border-cyan-100',
  },
  write_lor: {
    label: 'Writing LOR',
    icon: FilePenLine,
    activeClassName: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    idleClassName: 'bg-emerald-50 text-emerald-500 border-emerald-100',
  },
  get_lor_autofill: {
    label: 'Preparing LOR',
    icon: FilePenLine,
    activeClassName: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    idleClassName: 'bg-emerald-50 text-emerald-500 border-emerald-100',
  },
  write_coverage: {
    label: 'Updating coverage',
    icon: ShieldCheck,
    activeClassName: 'bg-violet-100 text-violet-700 border-violet-200',
    idleClassName: 'bg-violet-50 text-violet-500 border-violet-100',
  },
  read_coverage: {
    label: 'Reading coverage',
    icon: ShieldCheck,
    activeClassName: 'bg-violet-100 text-violet-700 border-violet-200',
    idleClassName: 'bg-violet-50 text-violet-500 border-violet-100',
  },
  store_open_claim_context: {
    label: 'Reading claim context',
    icon: FolderSearch,
    activeClassName: 'bg-indigo-100 text-indigo-700 border-indigo-200',
    idleClassName: 'bg-indigo-50 text-indigo-500 border-indigo-100',
  },
  list_files: {
    label: 'Reading files',
    icon: FolderSearch,
    activeClassName: 'bg-indigo-100 text-indigo-700 border-indigo-200',
    idleClassName: 'bg-indigo-50 text-indigo-500 border-indigo-100',
  },
  read_file: {
    label: 'Reading files',
    icon: FolderSearch,
    activeClassName: 'bg-indigo-100 text-indigo-700 border-indigo-200',
    idleClassName: 'bg-indigo-50 text-indigo-500 border-indigo-100',
  },
  update_task: {
    label: 'Updating task',
    icon: ClipboardCheck,
    activeClassName: 'bg-rose-100 text-rose-700 border-rose-200',
    idleClassName: 'bg-rose-50 text-rose-500 border-rose-100',
  },
  get_coverage_task_context: {
    label: 'Reading claim context',
    icon: FileSearch,
    activeClassName: 'bg-indigo-100 text-indigo-700 border-indigo-200',
    idleClassName: 'bg-indigo-50 text-indigo-500 border-indigo-100',
  },
  refresh_coverage_task_context: {
    label: 'Refreshing claim context',
    icon: Search,
    activeClassName: 'bg-indigo-100 text-indigo-700 border-indigo-200',
    idleClassName: 'bg-indigo-50 text-indigo-500 border-indigo-100',
  },
  get_lor_templates: {
    label: 'Loading templates',
    icon: Sparkles,
    activeClassName: 'bg-teal-100 text-teal-700 border-teal-200',
    idleClassName: 'bg-teal-50 text-teal-500 border-teal-100',
  },
  audit_lor_draft: {
    label: 'Auditing LOR',
    icon: ClipboardCheck,
    activeClassName: 'bg-lime-100 text-lime-700 border-lime-200',
    idleClassName: 'bg-lime-50 text-lime-500 border-lime-100',
  },
  audit_claims: {
    label: 'Auditing claim',
    icon: ShieldCheck,
    activeClassName: 'bg-lime-100 text-lime-700 border-lime-200',
    idleClassName: 'bg-lime-50 text-lime-500 border-lime-100',
  },
  send_slack_message: {
    label: 'Sending message',
    icon: MessageSquareMore,
    activeClassName: 'bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200',
    idleClassName: 'bg-fuchsia-50 text-fuchsia-500 border-fuchsia-100',
  },
  escalate_for_human_review: {
    label: 'Awaiting review',
    icon: Siren,
    activeClassName: 'bg-red-100 text-red-700 border-red-200',
    idleClassName: 'bg-red-50 text-red-500 border-red-100',
  },
  wait: {
    label: 'Waiting',
    icon: Clock3,
    activeClassName: 'bg-amber-100 text-amber-700 border-amber-200',
    idleClassName: 'bg-amber-50 text-amber-500 border-amber-100',
  },
};

const ACTIVE_WORKFLOW_STATUSES = new Set(['initializing', 'running', 'awaiting_review']);

const extractLatestToolFromTraces = (
  traces: Array<Record<string, unknown>> | null | undefined,
): { name: string | null; status: string | null } => {
  if (!traces?.length) {
    return { name: null, status: null };
  }

  let latestName: string | null = null;
  let latestStatus: string | null = null;

  traces.forEach((trace) => {
    const directName =
      typeof trace.tool_name === 'string'
        ? trace.tool_name
        : typeof trace.name === 'string'
          ? trace.name
          : null;
    if (directName) {
      latestName = directName;
      latestStatus =
        typeof trace.status === 'string'
          ? trace.status
          : typeof trace.tool_status === 'string'
            ? trace.tool_status
            : latestStatus;
    }

    const output =
      trace.output && typeof trace.output === 'object' && !Array.isArray(trace.output)
        ? (trace.output as Record<string, unknown>)
        : null;

    const toolExecutions = Array.isArray(output?.tool_executions)
      ? (output?.tool_executions as Array<Record<string, unknown>>)
      : [];
    if (toolExecutions.length > 0) {
      const lastExecution = toolExecutions[toolExecutions.length - 1];
      if (typeof lastExecution.tool_name === 'string') {
        latestName = lastExecution.tool_name;
        latestStatus =
          typeof lastExecution.status === 'string' ? lastExecution.status : latestStatus;
      }
    }

    const toolCalls = Array.isArray(output?.tool_calls)
      ? (output?.tool_calls as Array<Record<string, unknown>>)
      : [];
    if (toolCalls.length > 0) {
      const lastCall = toolCalls[toolCalls.length - 1];
      if (typeof lastCall.tool_name === 'string' || typeof lastCall.name === 'string') {
        latestName =
          typeof lastCall.tool_name === 'string' ? lastCall.tool_name : (lastCall.name as string);
        latestStatus =
          typeof lastCall.status === 'string'
            ? lastCall.status
            : typeof lastCall.tool_status === 'string'
              ? lastCall.tool_status
              : latestStatus;
      }
    }

    const children = Array.isArray(trace.children)
      ? (trace.children as Array<Record<string, unknown>>)
      : [];
    if (children.length > 0) {
      const childResult = extractLatestToolFromTraces(children);
      if (childResult.name) {
        latestName = childResult.name;
        latestStatus = childResult.status;
      }
    }
  });

  return { name: latestName, status: latestStatus };
};

const getResolvedStatus = (
  task: Task,
  latestToolName: string | null,
  latestToolStatus: string | null,
  hasPendingReview: boolean,
  liveWorkflowStatus?: string,
): AgentStatus | null => {
  const status = task.crashing_out_agent_status;
  if (!status) {
    return null;
  }
  if (liveWorkflowStatus && !ACTIVE_WORKFLOW_STATUSES.has(liveWorkflowStatus)) {
    return 'not_running';
  }
  if (
    hasPendingReview ||
    (latestToolName === 'escalate_for_human_review' && latestToolStatus === 'running')
  ) {
    return 'needs_review';
  }
  if (liveWorkflowStatus && ACTIVE_WORKFLOW_STATUSES.has(liveWorkflowStatus)) {
    return 'working';
  }
  return status;
};

const getStatusIcon = (
  latestToolName: string | null,
  status: AgentStatus,
) => {
  if (status === 'working') {
    const toolPresentation = latestToolName
      ? TOOL_PRESENTATIONS[latestToolName]
      : undefined;
    if (toolPresentation) {
      const ToolIcon = toolPresentation.icon;
      return (
        <ToolIcon className="h-4 w-4 animate-pulse" aria-hidden="true" />
      );
    }
  }

  switch (status) {
    case 'working':
      return (
        <LoaderCircle className="h-4 w-4 animate-spin text-amber-600" aria-hidden="true" />
      );
    case 'needs_review':
      return <BellRing className="h-4 w-4 text-red-700" aria-hidden="true" />;
    case 'not_running':
    default:
      return <MinusCircle className="h-4 w-4 text-zinc-400" aria-hidden="true" />;
  }
};

const getButtonClassName = (
  latestToolName: string | null,
  status: AgentStatus,
) => {
  if (status === 'needs_review') {
    return 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100';
  }

  if (status === 'working' && latestToolName) {
    return (
      TOOL_PRESENTATIONS[latestToolName]?.activeClassName ||
      'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100'
    );
  }

  if (status === 'not_running' && latestToolName) {
    return (
      TOOL_PRESENTATIONS[latestToolName]?.idleClassName ||
      'border-zinc-200 bg-zinc-50 text-zinc-500 hover:bg-zinc-100'
    );
  }

  return 'border-zinc-200 bg-zinc-50 text-zinc-500 hover:bg-zinc-100';
};

const getTooltipLabel = (
  latestToolName: string | null,
  status: AgentStatus,
) => {
  const toolLabel = latestToolName && TOOL_PRESENTATIONS[latestToolName]?.label;

  if (status === 'needs_review') {
    return toolLabel ? `${toolLabel} · Agent needs review` : STATUS_LABELS[status];
  }

  if (toolLabel) {
    return `${toolLabel} · ${STATUS_LABELS[status]}`;
  }

  return STATUS_LABELS[status];
};

export const CrashingOutAgentStatusCell = ({ task }: { task: Task }) => {
  const [open, setOpen] = useState(false);
  const [, setSearchParams] = useSearchParams();
  const { source } = useCrashingOutSource();
  const coverageId = task.coverage?.id ?? task.coverage_id;
  const matterId = task.matter;
  const shouldHydrateLiveStatus =
    source === 'temporal' &&
    Boolean(matterId && coverageId) &&
    task.crashing_out_agent_status !== 'not_running';

  const { data: liveStatus } = useGetCrashingOutStatusQuery(
    { matterId, coverageId: coverageId ?? '', source },
    {
      skip: !shouldHydrateLiveStatus || !coverageId,
      pollingInterval: 10000,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );
  const { data: livePendingReviews } = useGetCrashingOutPendingReviewsQuery(
    { matterId, coverageId: coverageId ?? '', source },
    {
      skip: !shouldHydrateLiveStatus || !coverageId,
      pollingInterval: 10000,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  const liveLatestTool = useMemo(
    () => extractLatestToolFromTraces((liveStatus?.traces as Array<Record<string, unknown>> | undefined) || []),
    [liveStatus?.traces],
  );

  const latestToolName =
    source === 'temporal'
      ? liveLatestTool.name ?? task.crashing_out_latest_tool ?? null
      : task.crashing_out_latest_tool ?? null;
  const latestToolStatus =
    source === 'temporal'
      ? liveLatestTool.status ?? task.crashing_out_latest_tool_status ?? null
      : task.crashing_out_latest_tool_status ?? null;
  const hasPendingReview = Boolean(livePendingReviews?.reviews?.length);
  const resolvedStatus =
    source === 'postgres'
      ? task.crashing_out_agent_status ?? null
      : getResolvedStatus(
          task,
          latestToolName,
          latestToolStatus,
          hasPendingReview,
          liveStatus?.workflow_status,
        );

  const stopRowInteraction = useCallback((event: SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const openRunDetails = useCallback(
    (event: SyntheticEvent) => {
      stopRowInteraction(event);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('task');
        next.delete('task_matter');
        return next;
      });
      setOpen(true);
    },
    [setSearchParams, stopRowInteraction],
  );

  if (!coverageId || !resolvedStatus) {
    return <span className="block h-4 w-4" />;
  }

  const label = getTooltipLabel(latestToolName, resolvedStatus);

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={label}
            data-stop-row-click="true"
            className={cn(
              'inline-flex h-8 w-8 items-center justify-center rounded-full border transition-colors',
              getButtonClassName(latestToolName, resolvedStatus),
            )}
            onPointerDownCapture={stopRowInteraction}
            onPointerDown={stopRowInteraction}
            onClickCapture={stopRowInteraction}
            onClick={openRunDetails}
          >
            {getStatusIcon(latestToolName, resolvedStatus)}
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs font-medium">
          {label}
        </TooltipContent>
      </Tooltip>

      <CrashingOutRunDetailsModal
        open={open}
        onOpenChange={setOpen}
        matterId={task.matter}
        coverageId={coverageId}
        initialDisplayStatus={resolvedStatus}
        initialMatterName={task.matter_name}
        initialCoverageName={task.coverage_carrier_name ?? task.coverage?.carrier?.name ?? null}
      />
    </>
  );
};
